import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Crown, Scales, ArrowRight, Fire } from "@phosphor-icons/react";

/* Champion Spotlight card — shown on the landing hero when a Moot Season has
 * been concluded. Silent no-op otherwise. */
export default function ChampionSpotlight() {
  const [champ, setChamp] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/legal-game/champion");
        if (!cancelled && r.data?.has_champion) setChamp(r.data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!champ) return null;

  return (
    <Card
      data-testid="champion-spotlight"
      className="relative overflow-hidden border-amber-500/40 bg-gradient-to-br from-amber-500/15 via-black to-emerald-500/10 mb-10"
    >
      {/* Ambient glow */}
      <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-10 w-60 h-60 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />

      <CardContent className="relative p-5 md:p-7 flex flex-col md:flex-row gap-6 items-center">
        <div className="flex-shrink-0 relative">
          {champ.picture ? (
            <img
              src={champ.picture}
              alt={champ.name}
              className="w-24 h-24 md:w-28 md:h-28 rounded-full object-cover border-2 border-amber-400/80"
            />
          ) : (
            <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-black/40 border-2 border-amber-400/80 flex items-center justify-center text-3xl font-black text-amber-300">
              {(champ.name || "?")[0]}
            </div>
          )}
          <div className="absolute -top-2 -right-2 rounded-full bg-amber-400 p-1.5 shadow-lg shadow-amber-500/40">
            <Crown size={16} weight="fill" className="text-black" />
          </div>
        </div>

        <div className="flex-1 min-w-0 space-y-2 text-center md:text-left">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-black text-amber-300">
            <Scales size={12} weight="fill" /> Reigning Moot Champion
          </div>
          <h3 className="font-heading text-2xl md:text-3xl font-black leading-tight">
            {champ.name}{" "}
            <span aria-label={champ.country} className="ml-1 text-2xl align-middle">{champ.flag}</span>
          </h3>
          <p className="text-sm text-white/70">
            Crowned in{" "}
            <span className="font-semibold text-white">{champ.season_name}</span>
            {champ.winning_subject && (
              <> · strongest in{" "}
                <span className="font-semibold text-emerald-300">{champ.winning_subject}</span></>
            )}
          </p>
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 pt-1">
            <Badge className="bg-amber-500/20 text-amber-200 border-amber-500/50 gap-1">
              <Trophy size={12} weight="fill" /> {champ.total_correct} correct
            </Badge>
            <Badge className="bg-orange-500/20 text-orange-200 border-orange-500/50 gap-1">
              <Fire size={12} weight="fill" /> Best streak {champ.highest_streak}
            </Badge>
            {(champ.badges || []).slice(0, 2).map(b => (
              <Badge key={b} className="bg-emerald-500/20 text-emerald-200 border-emerald-500/40">
                {b.replace(/_/g, " ")}
              </Badge>
            ))}
          </div>
        </div>

        <Link
          data-testid="champion-cta"
          to="/games/legal-championship"
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-amber-400 hover:bg-amber-300 text-black font-heading font-black text-sm transition"
        >
          Take the throne <ArrowRight size={14} weight="bold" />
        </Link>
      </CardContent>
    </Card>
  );
}
